import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// NOTE: The legal article bodies below are binding legal text (KVKK, cookies,
// privacy). Per the source-of-truth docs they must be authored/reviewed per
// language by legal counsel — they are deliberately NOT machine-translated.
// Only the page chrome (nav, back link, footer, titles) is i18n-wired. Each
// locale's lawyer-approved body is a separate localization track.

interface LegalProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

function LegalLayout({ title, lastUpdated, children }: LegalProps) {
  const { t } = useTranslation("public");
  return (
    <div className="landing">
      <header className="landing-nav">
        <Link to="/" className="brand" aria-label="AICE">
          <img src="/logo-mark.png" alt="AICE" className="brand-logo" />
        </Link>
        <nav>
          <Link to="/#cozumler">{t("nav.solutions")}</Link>
          <Link to="/#demo" className="cta">
            {t("nav.demo")}
          </Link>
        </nav>
      </header>
      <section className="section legal-page">
        <Link to="/" className="back-link">
          <span aria-hidden>&larr;</span> {t("nav.home")}
        </Link>
        <h1>{title}</h1>
        <p className="muted">{t("legal.lastUpdated", { date: lastUpdated })}</p>
        <div className="legal-body">{children}</div>
      </section>
      <footer className="landing-footer">
        <div className="footer-bottom">
          <span>&copy; {new Date().getFullYear()} AiCruise Eye</span>
          <div className="footer-legal">
            <Link to="/legal/kvkk">KVKK</Link>
            <Link to="/legal/cookies">{t("legal.cookiesTitle")}</Link>
            <Link to="/legal/privacy">{t("legal.privacyTitle")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function KvkkPage() {
  const { t } = useTranslation("public");
  return (
    <LegalLayout title={t("legal.kvkkTitle")} lastUpdated="2026-05-27">
      <p className="legal-callout">
        <strong>Önemli:</strong> Bu metin taslak niteliğindedir. Yayın öncesi
        kurumsal avukatınız tarafından gözden geçirilmeli ve VERBİS kaydınızla
        uyumlu hale getirilmelidir.
      </p>
      <h2>1. Veri Sorumlusu</h2>
      <p>
        AiCruise Eye 360 ("AICE") veri sorumlusu olarak, 6698 sayılı Kişisel
        Verilerin Korunması Kanunu ("KVKK") kapsamında, kişisel verilerinizin
        işlenmesi, saklanması ve aktarılması süreçlerinde aşağıda belirtilen
        ilkelere uygun hareket eder.
      </p>
      <h2>2. İşlenen Kişisel Veri Kategorileri</h2>
      <ul>
        <li>
          <strong>Kimlik bilgileri:</strong> Ad, soyad, T.C. kimlik numarası
          (sürücü kayıt için).
        </li>
        <li>
          <strong>İletişim bilgileri:</strong> Telefon, e-posta, adres.
        </li>
        <li>
          <strong>Konum/Hareket bilgileri:</strong> Araç GPS koordinatları, hız,
          rota.
        </li>
        <li>
          <strong>Biyometrik veriler (opsiyonel):</strong> Sürücü yüz tanıma,
          PERCLOS yorgunluk verileri — yalnızca sürücünün yazılı onayı ile.
        </li>
        <li>
          <strong>Görsel/İşitsel kayıtlar:</strong> Araç içi/dışı kamera
          görüntüleri, termal taramalar.
        </li>
      </ul>
      <h2>3. İşleme Amaçları</h2>
      <ul>
        <li>Filo güvenliği ve operasyonel verimlilik sağlanması.</li>
        <li>Sürücü iş güvenliği (yorgunluk, dikkat dağılımı tespiti).</li>
        <li>Yasal yükümlülüklerin (taşıma mevzuatı, gümrük) yerine getirilmesi.</li>
        <li>Hizmet kalitesinin iyileştirilmesi ve müşteri desteği.</li>
      </ul>
      <h2>4. Veri Saklama Süresi</h2>
      <p>
        Konum/telemetri verileri 12 ay, görüntü kayıtları 30 gün (olay yoksa),
        olay/alarm verileri 5 yıl saklanır. Sürücü biyometrik verileri çalışma
        ilişkisinin sona ermesini takiben 30 gün içinde silinir.
      </p>
      <h2>5. Haklarınız</h2>
      <p>
        KVKK madde 11 kapsamında verilerinize erişim, düzeltme, silme, işlemeye
        itiraz ve veri taşınabilirliği haklarına sahipsiniz. Talepleriniz için:{" "}
        <a href="mailto:kvkk@aicruiseeye.com">kvkk@aicruiseeye.com</a>.
      </p>
      <h2>6. Veri Aktarımı</h2>
      <p>
        Verileriniz, hizmetin sunulması için zorunlu olduğu ölçüde bulut
        sağlayıcımıza (Türkiye sınırları içinde) aktarılır. Yurt dışına veri
        aktarımı yapılmaz.
      </p>
    </LegalLayout>
  );
}

export function CookiesPage() {
  const { t } = useTranslation("public");
  return (
    <LegalLayout title={t("legal.cookiesTitle")} lastUpdated="2026-05-27">
      <h2>Hangi Çerezleri Kullanıyoruz?</h2>
      <ul>
        <li>
          <strong>Zorunlu çerezler:</strong> Oturum yönetimi (JWT access token —
          localStorage). Bunlar olmadan panel çalışmaz.
        </li>
        <li>
          <strong>Performans çerezleri:</strong> Kullanılmıyor. Google
          Analytics, Hotjar vb. üçüncü taraf izleme yok.
        </li>
        <li>
          <strong>Reklam çerezleri:</strong> Kullanılmıyor.
        </li>
      </ul>
      <h2>Çerezleri Nasıl Yönetebilirsiniz?</h2>
      <p>
        Tarayıcınızın geliştirici araçlarından (Application &gt; Local Storage)
        AICE çerezlerini silebilirsiniz. Bu işlem, oturumunuzu sonlandırır ve
        tekrar giriş yapmanız gerekir.
      </p>
      <h2>Üçüncü Taraf Servisler</h2>
      <p>
        Harita bileşeni <strong>OpenStreetMap</strong> tile sunucusunu kullanır;
        bu sunucu IP adresinizi loglayabilir. AICE harita üzerinden hiçbir
        kişisel veri OSM'ye gönderilmez.
      </p>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  const { t } = useTranslation("public");
  return (
    <LegalLayout title={t("legal.privacyTitle")} lastUpdated="2026-05-27">
      <p className="legal-callout">
        <strong>Taslak:</strong> Bu doküman, KVKK uyumlu son haliyle
        yayınlanmadan önce hukuki onaydan geçecektir.
      </p>
      <h2>Güvenlik Önlemleri</h2>
      <ul>
        <li>TLS 1.3 (Let's Encrypt) — tüm trafik şifreli.</li>
        <li>JWT + RBAC erişim kontrolü; her firma kendi izole DB şemasına sahip.</li>
        <li>Parolalar stdlib scrypt ile hash'lenir; düz metin saklanmaz.</li>
        <li>RabbitMQ, PostgreSQL, InfluxDB yalnızca localhost'a bind edilmiş; public erişim yok.</li>
        <li>Sırlar (API anahtarları, SMTP creds) per-firm settings tablosunda maskeli saklanır.</li>
      </ul>
      <h2>İhlal Bildirimi</h2>
      <p>
        Veri ihlali tespit edilirse KVKK madde 12/5 kapsamında 72 saat içinde
        Kişisel Verileri Koruma Kurulu'na ve etkilenen veri sahiplerine bildirim
        yapılır.
      </p>
      <h2>İletişim</h2>
      <p>
        Güvenlik açığı bildirimi:{" "}
        <a href="mailto:security@aicruiseeye.com">security@aicruiseeye.com</a>
      </p>
    </LegalLayout>
  );
}
